import React from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, TextField, Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TagSelector from '../TagSelector';
import ImageSelector from '../quizzes/ImageSelector';
import { LOCK_ASPECT_SHARE } from '../Const';
import FanPollOptionsEditor from './FanPollOptionsEditor';
import FanPollScheduleEditor from './FanPollScheduleEditor';
import FanPollTopicSelector from './FanPollTopicSelector';
import { ensureMainTagInTags } from './fanPollModel';

export default function FanPollEditor({
    draft, errors, onChange, onTopicSelected, onGenerateAI, generatingAI, onGenerateTags, generatingTags,
    onInspire, inspiring
}) {
    const set = (field, value) => onChange({ ...draft, [field]: value });

    const handleMainTagChange = (mainTag) => onTopicSelected({
        ...draft,
        mainTag,
        tagList: ensureMainTagInTags(mainTag, draft.tagList),
    });

    const handleTagsChange = (tagList) => {
        const mainStillSelected = !draft.mainTag || (tagList ?? []).some(tag => (
            tag?.idMapping?.WIKIDATA && draft.mainTag?.idMapping?.WIKIDATA
                ? tag.idMapping.WIKIDATA === draft.mainTag.idMapping.WIKIDATA
                : tag?.label?.trim().toLowerCase() === draft.mainTag?.label?.trim().toLowerCase()
        ));
        const nextDraft = { ...draft, tagList, mainTag: mainStillSelected ? draft.mainTag : null };
        if (mainStillSelected) onChange(nextDraft);
        else onTopicSelected(nextDraft);
    };

    const handleScheduleChange = (field, value) => set(field, value);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.keys(errors ?? {}).length > 0 && (
                <Alert severity="error">Please correct the highlighted poll fields before saving.</Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Poll setup</Typography>
                <FanPollTopicSelector draft={draft} errors={errors} onChange={onChange} onTopicSelected={onTopicSelected}
                    onInspire={onInspire} inspiring={inspiring} />
                <Button
                    sx={{ mt: 1.5 }}
                    variant="contained"
                    startIcon={generatingAI ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={onGenerateAI}
                    disabled={generatingAI || !draft.mainTag || !draft.subject?.trim()}
                >
                    Generate AI poll text
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Generates the display title, question, description, SEO slug, and one answer per current option row with OpenAI, then validates them with Claude.
                </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Basic information</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField fullWidth size="small" label="Admin/display title" value={draft.label}
                        onChange={event => set('label', event.target.value)} error={Boolean(errors.label)} helperText={errors.label} />
                    <TextField fullWidth size="small" label="Poll question" value={draft.question}
                        onChange={event => set('question', event.target.value)} error={Boolean(errors.question)} helperText={errors.question} />
                    <TextField fullWidth size="small" multiline minRows={2} label="Description" value={draft.description}
                        onChange={event => set('description', event.target.value)} />
                    <TextField size="small" label="SEO slug" value={draft.seoTitle}
                        disabled={Boolean(draft.id)} onChange={event => set('seoTitle', event.target.value)}
                        helperText={draft.id ? 'The slug is fixed after the first save.' : 'Leave blank to generate from the title.'} />
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>Primary topic and tags</Typography>
                <Typography variant="caption" color={errors.mainTag ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 1 }}>
                    {errors.mainTag || 'Select tags, then click one tag to make it the primary artist or genre.'}
                </Typography>
                <TagSelector mainTag={draft.mainTag} selected={draft.tagList}
                    onMainTagChange={handleMainTagChange} onTagValuesChange={handleTagsChange} />
                <Button size="small" sx={{ mt: 0.5 }} onClick={onGenerateTags}
                    disabled={generatingTags || !draft.mainTag}
                    startIcon={generatingTags ? <CircularProgress size={16} /> : undefined}>
                    Generate tags
                </Button>
            </Paper>

            <FanPollOptionsEditor options={draft.options} error={errors.options} onChange={options => set('options', options)} />

            <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                    control={<Checkbox checked={draft.allowSuggestion} onChange={event => set('allowSuggestion', event.target.checked)} />}
                    label="Allow a free-text answer"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Free text is stored as a pending suggestion and is not automatically shown to other voters.
                </Typography>
            </Paper>

            <FanPollScheduleEditor startDate={draft.startDate} endDate={draft.endDate}
                endDateError={errors.endDate} onChange={handleScheduleChange} />

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>Poll photo</Typography>
                {errors.imageMetadata && <Typography variant="caption" color="error">{errors.imageMetadata}</Typography>}
                <Box sx={{ mt: 1 }}>
                    <ImageSelector
                        imd={draft.imageMetadata}
                        lockAspect={LOCK_ASPECT_SHARE}
                        searchStr={draft.mainTag?.label ? `"${draft.mainTag.label}"` : null}
                        onSelect={imageMetadata => set('imageMetadata', imageMetadata)}
                        onAltChange={alt => set('imageMetadata', draft.imageMetadata ? { ...draft.imageMetadata, alt } : null)}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
